import {createFileRoute, redirect, useLocation, useNavigate} from "@tanstack/react-router";
import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {IndicacaoService, UserService} from "@/services";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage,} from "../../components/ui/form";
import {Logo} from "@/components/core/logo";
import {formatCpfInput, formatDate, formatPhoneInput, toIsoDate, validateAge, validateCPF,} from "@/lib/utils";
import {
  REFERRAL_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "@/config/base";
import {showToast} from "@/hooks/useToast";

export const Route = createFileRoute("/auth/register")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (token && user) {
      throw redirect({
        to: "/app",
      });
    }
  },
  component: RouteComponent,
});

const registerSchema = z
  .object({
    nome: z.string().min(1, "Nome é obrigatório"),
    email: z.string().email("Informe um e-mail válido"),
    telefone: z
      .string()
      .min(1, "Telefone é obrigatório")
      .refine((value) => {
        const digits = value.replace(/[^0-9]+/g, "");
        return digits.length === 10 || digits.length === 11;
      }, "Telefone deve ter 10 ou 11 dígitos (com DDD)"),
    cpf: z
      .string()
      .min(1, "CPF é obrigatório")
      .refine(
        (value) => value.replace(/[^0-9]+/g, "").length === 11,
        "CPF deve ter 11 dígitos"
      )
      .refine((value) => validateCPF(value), "CPF inválido"),
    dataNascimento: z.string().min(1, "Data de nascimento é obrigatória"),
    codigoIndicacao: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9]{8}$/, "Código deve ter 8 caracteres")
      .optional()
      .or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    const result = validateAge(data.dataNascimento);
    if (!result.valid && result.age !== undefined) {
      if (result.age < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Você deve ter pelo menos 18 anos",
          path: ["dataNascimento"],
        });
      } else if (result.age > 120) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Data de nascimento inválida",
          path: ["dataNascimento"],
        });
      }
    }
  });

function RouteComponent() {
  const navigate = useNavigate();
  const {search} = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [indicadorNome, setIndicadorNome] = useState<string | null>(null);

  // Pré-preenche os campos com dados do Google se disponíveis
  const googleData = search as { nome?: string; email?: string; foto?: string; ref?: string };
  const isFromGoogle = !!googleData?.email;
  const queryRefCode = googleData?.ref?.trim().toUpperCase() ?? "";
  const initialRefCode =
    queryRefCode || (sessionStorage.getItem(REFERRAL_STORAGE_KEY) ?? "");

  const form = useForm<z.infer<typeof registerSchema>>({
    // @ts-ignore
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: googleData?.nome || "",
      email: googleData?.email || "",
      telefone: "",
      cpf: "",
      dataNascimento: "",
      codigoIndicacao: initialRefCode,
    },
  });

  useEffect(() => {
    if (!initialRefCode) {
      return;
    }

    if (/^[A-Z0-9]{8}$/.test(initialRefCode)) {
      sessionStorage.setItem(REFERRAL_STORAGE_KEY, initialRefCode);
    }
  }, [initialRefCode]);

  const validateReferralCode = async (rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) {
      setIndicadorNome(null);
      return true;
    }

    try {
      const validation = await IndicacaoService.validarCodigo(code);
      if (!validation.valido) {
        form.setError("codigoIndicacao", {
          type: "manual",
          message: "Código de indicação inválido",
        });
        setIndicadorNome(null);
        return false;
      }

      setIndicadorNome(validation.nomeIndicador ?? null);
      return true;
    } catch {
      // Não bloqueia cadastro por falha de rede na validação prévia.
      setIndicadorNome(null);
      return true;
    }
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsLoading(true);
    try {
      const codigoIndicacao = values.codigoIndicacao?.trim().toUpperCase();
      const isReferralValid = await validateReferralCode(codigoIndicacao ?? "");
      if (!isReferralValid) {
        setIsLoading(false);
        return;
      }

      const sanitizedData = {
        nome: values.nome,
        email: values.email,
        telefone: values.telefone.replace(/[^0-9]+/g, ""),
        cpf: values.cpf.replace(/[^0-9]+/g, ""),
        dataNascimento: toIsoDate(values.dataNascimento),
        codigoIndicacao: codigoIndicacao || undefined,
      };

      await UserService.create(sanitizedData);
      sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
      showToast.success("Conta criada! Verifique seu e-mail para ativar a conta.");
      setSuccess(true);
      setTimeout(() => {
        navigate({
          to: "/auth/verify-phone" as any,
          // @ts-ignore
          search: {telefone: sanitizedData.telefone}
        });
      }, 2000);
    } catch (err) {
      showToast.error(
        err instanceof Error ? err.message : "Não foi possível criar a conta"
      );
    } finally {
      setIsLoading(false);
    }
  });

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `url("/authentication-bg.svg")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="w-full max-w-md relative z-10">
          <div className="bg-white rounded-4xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Conta criada com sucesso!
            </h2>
            <p className="text-gray-600 mb-2">Redirecionando para verificação do telefone...</p>
            {!isFromGoogle && (
              <p className="text-gray-500 text-sm">Também enviamos um link de ativação para o seu e-mail.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url("/authentication-bg.svg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-4xl shadow-xl p-8">
          <div className="flex items-center justify-center mb-6">
            <Logo className="h-12" isTraditionalLogo/>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Criar sua conta
            </h1>
            <p className="text-sm text-gray-600">
              Preencha seus dados para começar a usar o JapExpress
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField
                control={form.control}
                name="nome"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Nome completo</FormLabel>
                    <FormControl>
                      <Input placeholder="João da Silva" {...field} />
                    </FormControl>
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="seuemail@exemplo.com"
                        {...field}
                        readOnly={isFromGoogle}
                        disabled={isFromGoogle}
                      />
                    </FormControl>
                    {isFromGoogle && (
                      <p className="text-xs text-gray-500 mt-1">E-mail vinculado ao Google e não pode ser alterado</p>
                    )}
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="telefone"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="tel"
                        placeholder="(00) 00000-0000"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatPhoneInput(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cpf"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>CPF</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder="000.000.000-00"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatCpfInput(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dataNascimento"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Data de nascimento</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder="12/11/2002"
                        value={field.value}
                        onChange={(event) =>
                          field.onChange(formatDate(event.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="codigoIndicacao"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Código de indicação (opcional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ABC12345"
                        value={field.value}
                        onChange={(event) => {
                          const nextValue = event.target.value
                            .replace(/[^A-Za-z0-9]/g, "")
                            .toUpperCase()
                            .slice(0, 8);
                          field.onChange(nextValue);
                        }}
                        onBlur={async () => {
                          await validateReferralCode(field.value || "");
                        }}
                      />
                    </FormControl>
                    {indicadorNome && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Indicação válida de {indicadorNome}
                      </p>
                    )}
                    <FormMessage/>
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Criando conta..." : "Criar conta"}
              </Button>
            </form>
          </Form>

          <div className="mt-6 text-center text-xs text-gray-400">
            Ao continuar, você concorda com os termos de uso e a política de
            privacidade da JaPediu.
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate({to: "/auth/identify" as any})}
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Já tem uma conta?{" "}
              <span className="font-semibold text-blue-600">Faça login</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
